/**
 * The assistant drawer's state, for React.
 *
 * One provider holds four things that would otherwise be scattered:
 *
 * - **Whether the drawer is open, and how wide.** ⌘J toggles it; the width is
 *   remembered in `localStorage` like the sidebar's collapse.
 * - **Which thread is showing.** Thread ids are *derived* — `thread-recipe-<id>`
 *   in the editor, `thread-blend-<id>` on a blending session, `thread-global`
 *   everywhere else — so the per-document thread auto-selects with no
 *   bookkeeping and survives a reload.
 * - **The transcript**, which is the persisted history plus whatever is
 *   streaming right now, in one array the message list renders straight down.
 * - **A prefill**, so the explain popover's "Ask the assistant" link and
 *   "Generate recipe" can open the drawer with a targeted question already
 *   typed and the numbers in it, rather than making the user restate what
 *   the screen already shows.
 *
 * Accepting a proposal is deliberately *not* here — it goes through the ordinary
 * store dispatch from the card itself (PLAN §7.1), so the assistant's writes are
 * the user's writes and undo, sync and history need no special case.
 */

import type { ReactNode } from 'react';
import { createContext, use, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSession } from '@/auth/session-context';
import type { AiAllowance } from '@/lib/plan';
import type { AiRequestError } from './client';
import { deleteThread, fetchAllowance, fetchThread, streamChat, threadIdFor } from './client';
import type { AiStreamEvent, AiThreadEntry, AiThreadEntryInput } from './protocol';
import { STOPPED_NOTICE } from './protocol';

/** `localStorage` keys. Namespaced like the rest of the app's UI state. */
const WIDTH_KEY = 'beerwright.assistant-width';
const OPEN_KEY = 'beerwright.assistant-open';
const RESOLVED_KEY = 'beerwright.assistant-resolved';

export const MIN_DRAWER_WIDTH = 320;
export const MAX_DRAWER_WIDTH = 720;
const DEFAULT_DRAWER_WIDTH = 420;

/**
 * The narrowest window the drawer docks in: Tailwind's `xl`, where
 * `assistant-drawer.tsx` switches from covering the screen to sitting beside
 * the workspace and draws its resize gutter.
 *
 * Here in TypeScript as well as in the class names because the arithmetic
 * below has to agree with it. It did not, once: the gutter appeared at 1280
 * and {@link clampDrawerWidth} could not return a second distinct number until
 * 1568, so on every laptop there is the handle took the pointer, highlighted
 * under it, and moved nothing. A test pins the relationship now.
 */
export const DRAWER_DOCKS_AT = 1280;

/**
 * What the workspace behind a docked drawer keeps before the drawer may take
 * any more: the expanded nav at 240 (`w-60`), the vitals rail at 320
 * (`xl:w-80`), the page's own padding at 48 (`sm:px-6`, both sides), and 240
 * of the recipe editor's table.
 *
 * **The table's share is a slice, not its `xl:min-w-[40rem]`**, and that is the
 * whole of what changed on 2026-09-21. Counting the full 640 put this at 1248,
 * which with the drawer's own 320 minimum came to 1568 — 288 px past the point
 * where the handle is drawn, so between those two numbers the ceiling sat on
 * the floor and the drawer was frozen at 320. The floor was not protecting the
 * tables there in any case: the old note said as much, that in that band "the
 * editor's tables scroll inside their box". They scrolled, and the brewer got
 * no say in how much.
 *
 * So the number is now what the workspace keeps *whatever the brewer does*,
 * and how much beyond that the editor gets is the brewer's to drag. Past
 * {@link DRAWER_DOCKS_AT} there is real travel at every width; below it the
 * drawer covers the screen and this number has nothing to say. Still a floor
 * on the harm rather than a promise that everything fits — and one the brewer
 * can now reach for, which is the point.
 */
export const MIN_WORKSPACE_WIDTH = 848;

/**
 * The remembered width, fitted to the window in front of us.
 *
 * Clamped *at use*, never written back: a brewer who dragged the drawer out to
 * 640 on a desktop still has 640 when they return to that desk, even after a
 * session on a narrower screen where it could only show 400. A viewport that is
 * not a finite number — no `window` to measure yet — applies the fixed bounds
 * alone rather than guessing at a ceiling.
 */
export function clampDrawerWidth(width: number, viewportWidth: number): number {
  const ceiling = Number.isFinite(viewportWidth)
    ? Math.max(MIN_DRAWER_WIDTH, Math.min(MAX_DRAWER_WIDTH, viewportWidth - MIN_WORKSPACE_WIDTH))
    : MAX_DRAWER_WIDTH;
  return Math.min(ceiling, Math.max(MIN_DRAWER_WIDTH, width));
}

/** How a proposal was resolved, once the user has decided. */
export type ProposalOutcome = 'accepted' | 'rejected';

/** A transcript entry that has not been persisted yet — this turn's live output. */
type LiveEntry = AiThreadEntryInput & { readonly id: number; readonly at: number };

export interface AssistantState {
  readonly open: boolean;
  readonly setOpen: (open: boolean) => void;
  readonly toggle: () => void;
  readonly width: number;
  readonly setWidth: (width: number) => void;

  /** The recipe in view. The editor sets it; everything else clears it. */
  readonly recipeId: string | null;
  readonly setRecipeId: (recipeId: string | null) => void;
  /** The blending session in view. The blender sets it; everything else clears it. */
  readonly blendId: string | null;
  readonly setBlendId: (blendId: string | null) => void;
  /** The batch in view. The batch page and its Feedback page set it. */
  readonly batchId: string | null;
  readonly setBatchId: (batchId: string | null) => void;
  readonly threadId: string;

  readonly entries: readonly AiThreadEntry[];
  /** An answer is arriving on the thread on screen. */
  readonly streaming: boolean;
  /**
   * An answer is arriving on *some* thread — this one, or the one the brewer
   * asked in before they moved to another page. One turn at a time, so the
   * composer waits for it either way.
   */
  readonly busy: boolean;
  /** The assistant text arriving right now, before it lands as an entry. */
  readonly liveText: string;
  /**
   * Whether the turn in flight has gone quiet: streaming, and either nothing
   * written yet in this step or no new words for {@link TEXT_QUIET_MS}.
   *
   * The second half is the one that was missing. A model writes a sentence and
   * then composes its next call in silence — for a proposal that is the whole
   * recipe — and with the sentence on screen nothing said the turn was alive.
   */
  readonly thinking: boolean;
  /** A pre-stream failure — quota, or an unconfigured deployment. */
  readonly requestError: AiRequestError | null;
  /**
   * This month's assistant allowance, as the server counts it (PLAN §9).
   *
   * `null` until it has been read, and for a guest or an unconfigured
   * deployment — neither of which has an allowance to report. Refreshed after
   * every turn, and replaced outright by the 429's copy when one arrives, so the
   * sidebar meter and the drawer's exhausted state never disagree.
   */
  readonly allowance: AiAllowance | null;

  readonly draft: string;
  readonly setDraft: (draft: string) => void;
  readonly send: (message: string) => void;

  /**
   * Withdraw the turn that is streaming.
   *
   * A withdrawal rather than a cancellation: the words that arrived stay on
   * screen with a line saying where they stop, the server persists the same
   * thing, and the message stays spent — refunding a stop would make every
   * answer free to whoever reads quickly and presses the button before the end.
   * A no-op when nothing is streaming.
   */
  readonly stop: () => void;

  /**
   * Start this conversation again: delete it, server side, and empty the drawer.
   *
   * A no-op while a turn is streaming — the answer arriving would be written
   * into a thread that had just been deleted, and the button that calls this is
   * disabled for the same reason. Nothing is re-fetched afterwards, because the
   * answer is known: an empty thread.
   */
  readonly clearThread: () => void;

  /**
   * Open the drawer with a question already written.
   *
   * @param message - The prompt, with the numbers already in it.
   * @param recipeId - Switch to that recipe's thread first.
   * @param batchId - Switch to that batch's thread first.
   * @param autoSend - Send immediately rather than letting the user edit it.
   */
  readonly ask: (
    message: string,
    options?: { recipeId?: string; batchId?: string; autoSend?: boolean },
  ) => void;

  readonly outcomeOf: (changesetId: string) => ProposalOutcome | null;
  readonly resolve: (changesetId: string, outcome: ProposalOutcome) => void;
}

const AssistantContext = createContext<AssistantState | null>(null);

/** How long without a new word before a streaming answer reads as thinking again. */
const TEXT_QUIET_MS = 1500;

export function AssistantProvider({ children }: { readonly children: ReactNode }) {
  const session = useSession();
  const [open, setOpen] = useState(() => readFlag(OPEN_KEY));
  const [width, setWidth] = useState(() => readWidth());
  const [recipeId, setRecipeId] = useState<string | null>(null);
  const [blendId, setBlendId] = useState<string | null>(null);
  const [batchId, setBatchId] = useState<string | null>(null);
  const [entries, setEntries] = useState<readonly AiThreadEntry[]>([]);
  const [live, setLive] = useState<readonly LiveEntry[]>([]);
  const [liveText, setLiveText] = useState('');
  /** True once the live text has had no new word for {@link TEXT_QUIET_MS}. */
  const [textQuiet, setTextQuiet] = useState(false);
  const quietTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [requestError, setRequestError] = useState<AiRequestError | null>(null);
  /**
   * The thread the turn in flight was asked in, or `null` between turns.
   *
   * The drawer follows the page, but a turn does not: a brewer who clicks into
   * a recipe while the workspace thread is still answering has changed what is
   * on screen, not where the answer goes. Without this, the words still
   * arriving were drawn into the recipe's conversation, and the re-read when
   * the turn landed replaced the recipe's transcript with the other one.
   */
  const [turnThread, setTurnThread] = useState<string | null>(null);
  /**
   * The thread `live`, `liveText` and `requestError` belong to — the last one
   * a turn was asked in. They are shown only while it is the one on screen.
   */
  const [liveThread, setLiveThread] = useState<string | null>(null);
  const [allowance, setAllowance] = useState<AiAllowance | null>(null);
  const [draft, setDraft] = useState('');
  const [resolved, setResolved] = useState<Record<string, ProposalOutcome>>(readResolved);
  /**
   * A message `ask` queued to send itself.
   *
   * State rather than a ref, and that is the whole trick: an entry point that
   * fires while the drawer is already open changes no other state, so a ref
   * would set a value that nothing re-rendered to notice and the message would
   * never leave. Queuing through state also lets the send wait for a thread
   * switch — `send` changes identity with `threadId`, so the effect below runs
   * again once the new thread is the live one.
   */
  const [queued, setQueued] = useState<string | null>(null);
  /**
   * The turn in flight, so the Stop button has something to abort.
   *
   * A ref rather than state: nothing renders differently for *which* controller
   * is current, only for whether a turn is streaming, which `streaming` already
   * says. Cleared when the turn settles so a late press cannot abort a turn that
   * has already landed.
   */
  const turn = useRef<AbortController | null>(null);
  /**
   * Ids for entries that have not been persisted yet.
   *
   * Negative, so they cannot collide with the storage-assigned ones, and held
   * across sends rather than restarted per turn: `live` can still hold the
   * previous turn when the next one begins — a stopped answer stays on screen
   * until a re-read replaces it — and two turns that both counted down from −1
   * would give React the same key twice.
   */
  const liveSequence = useRef(0);

  const threadId = threadIdFor(recipeId, blendId, batchId);
  const busy = turnThread !== null;
  const streaming = turnThread === threadId;
  const liveShown = liveThread === threadId;
  /**
   * The thread on screen, for a turn to check once it lands. A ref because
   * the turn's callbacks were made when it was asked, and the state they
   * closed over is the thread it was asked in.
   */
  const shownThread = useRef(threadId);
  const signedIn = session.status === 'authenticated';
  const configured = session.config.ai.mode !== 'off';

  useEffect(() => {
    write(OPEN_KEY, String(open));
  }, [open]);
  useEffect(() => {
    write(WIDTH_KEY, String(width));
  }, [width]);

  // Load the thread when it changes. Keyed on the id rather than on `recipeId`
  // so the global thread and a recipe thread are one code path.
  useEffect(() => {
    shownThread.current = threadId;
    if (!signedIn || !configured) {
      setEntries([]);
      return;
    }
    let cancelled = false;
    // A turn still arriving keeps what it has so far, drawn only on its own
    // thread: coming back to it mid-answer finds the answer still being
    // written, not the stored transcript without it.
    if (turn.current === null) {
      setLive([]);
      setLiveText('');
      setRequestError(null);
    }
    void fetchThread(threadId).then((loaded) => {
      if (!cancelled) setEntries(loaded);
    });
    return () => {
      cancelled = true;
    };
  }, [threadId, signedIn, configured]);

  // The allowance is read once the assistant is usable, and after every turn.
  // Not on a timer: it only changes when this browser sends something, or when
  // another tab does — and the next turn's refresh catches that.
  useEffect(() => {
    if (!signedIn || !configured) {
      setAllowance(null);
      return;
    }
    let cancelled = false;
    void fetchAllowance().then((next) => {
      if (!cancelled && next !== null) setAllowance(next);
    });
    return () => {
      cancelled = true;
    };
  }, [signedIn, configured]);

  const send = useCallback(
    (message: string) => {
      const trimmed = message.trim();
      if (trimmed === '' || busy || !signedIn || !configured) return;

      setDraft('');
      setRequestError(null);
      setTurnThread(threadId);
      setLiveThread(threadId);
      const controller = new AbortController();
      turn.current = controller;
      // The user's own message is shown immediately; the server echoes it back
      // into the persisted transcript when the turn lands.
      const nextId = () => {
        liveSequence.current -= 1;
        return liveSequence.current;
      };
      // A question that never got an answer is the one thing a new turn takes
      // the place of: Try again re-sends it, and drawing it twice would be the
      // drawer inventing a message nobody typed. Everything else still in
      // `live` — a stopped turn's partial answer and the line that ended it —
      // stays where it is, because no re-fetch has replaced it yet and losing
      // it would be the drawer forgetting what the brewer just read.
      // What is left in `live` from a turn on another thread is that thread's,
      // and starts no part of this one.
      setLive((current) => [
        ...(liveShown ? withoutUnansweredQuestion(current) : []),
        { type: 'user', text: trimmed, id: nextId(), at: Date.now() },
      ]);
      setLiveText('');

      let pendingText = '';
      const flushText = () => {
        if (pendingText === '') return;
        const text = pendingText;
        pendingText = '';
        setLive((current) => [
          ...current,
          { type: 'assistant', text, id: nextId(), at: Date.now() },
        ]);
        setLiveText('');
      };

      const onEvent = (event: AiStreamEvent) => {
        switch (event.type) {
          case 'text_delta':
            pendingText += event.text;
            setLiveText(pendingText);
            setTextQuiet(false);
            if (quietTimer.current !== null) clearTimeout(quietTimer.current);
            quietTimer.current = setTimeout(() => setTextQuiet(true), TEXT_QUIET_MS);
            break;
          case 'tool_activity':
            flushText();
            setLive((current) => [
              ...current,
              {
                type: 'activity',
                tool: event.tool,
                summary: event.summary,
                failed: event.failed,
                id: nextId(),
                at: Date.now(),
              },
            ]);
            break;
          case 'proposal':
            flushText();
            setLive((current) => [
              ...current,
              { type: 'proposal', proposal: event.proposal, id: nextId(), at: Date.now() },
            ]);
            break;
          // A failure, drawn as one: the same notice the server is storing for
          // this turn, rather than the assistant appearing to have said "the
          // service could not be reached" in its own voice.
          case 'error':
            flushText();
            setLive((current) => [
              ...current,
              {
                type: 'notice',
                reason: 'failed',
                text: event.message,
                id: nextId(),
                at: Date.now(),
              },
            ]);
            break;
          case 'done':
            flushText();
            break;
        }
      };

      void streamChat(
        { threadId, recipeId, blendId, batchId, message: trimmed },
        onEvent,
        controller.signal,
      )
        .then(async () => {
          // Re-read the thread so the live entries are replaced by the stored
          // ones — same content, real ids, and proof it was persisted. Only
          // if it is still the one on screen: a brewer who has moved to
          // another page is looking at another transcript, and this one is
          // read afresh when they come back to it.
          const stored = await fetchThread(threadId);
          if (stored.length > 0 && shownThread.current === threadId) {
            setEntries(stored);
            setLive([]);
          }
          const spent = await fetchAllowance();
          if (spent !== null) setAllowance(spent);
        })
        .catch((error: unknown) => {
          // Whatever else happened, the words that did arrive are the brewer's
          // to keep: flushing here is what stops `finally` throwing them away.
          flushText();
          if (isAbortError(error)) {
            // Neither success nor failure — a withdrawal. No re-fetch, because
            // the server's own write of this turn lands under `waitUntil` and a
            // read racing it would show the turn missing; the next time this
            // thread is opened it comes back in exactly this shape.
            setLive((current) => [
              ...current,
              {
                type: 'notice',
                reason: 'stopped',
                text: STOPPED_NOTICE,
                id: nextId(),
                at: Date.now(),
              },
            ]);
            return;
          }
          const failure = asRequestError(error);
          setRequestError(failure);
          // A refusal already carries the count that refused it; taking it from
          // here means the drawer's "0 left, back on 1 September" is the
          // server's own arithmetic rather than the client's guess at it.
          if (failure.allowance !== undefined) setAllowance(failure.allowance);
        })
        .finally(() => {
          if (turn.current === controller) turn.current = null;
          setTurnThread(null);
          setLiveText('');
        });
    },
    [threadId, recipeId, blendId, batchId, busy, liveShown, signedIn, configured],
  );

  const stop = useCallback(() => {
    turn.current?.abort();
  }, []);

  const clearThread = useCallback(() => {
    if (streaming) return;
    void deleteThread(threadId).then((gone) => {
      if (!gone) return;
      setEntries([]);
      setLive([]);
      setLiveText('');
      setRequestError(null);
    });
  }, [threadId, streaming]);

  useEffect(() => {
    if (queued === null || busy) return;
    setQueued(null);
    send(queued);
  }, [queued, send, busy]);

  const ask = useCallback<AssistantState['ask']>((message, options = {}) => {
    setOpen(true);
    if (options.recipeId !== undefined) setRecipeId(options.recipeId);
    if (options.batchId !== undefined) setBatchId(options.batchId);
    if (options.autoSend === true) setQueued(message);
    else setDraft(message);
  }, []);

  const resolve = useCallback((changesetId: string, outcome: ProposalOutcome) => {
    setResolved((current) => {
      const next = { ...current, [changesetId]: outcome };
      write(RESOLVED_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const outcomeOf = useCallback(
    (changesetId: string): ProposalOutcome | null => resolved[changesetId] ?? null,
    [resolved],
  );

  const merged = useMemo<readonly AiThreadEntry[]>(
    () => (liveShown ? [...entries, ...(live as readonly AiThreadEntry[])] : entries),
    [entries, live, liveShown],
  );

  const value = useMemo<AssistantState>(
    () => ({
      open,
      setOpen,
      toggle: () => setOpen((current) => !current),
      width,
      setWidth,
      recipeId,
      setRecipeId,
      blendId,
      setBlendId,
      batchId,
      setBatchId,
      threadId,
      entries: merged,
      streaming,
      busy,
      liveText: liveShown ? liveText : '',
      thinking: streaming && (liveText === '' || textQuiet),
      requestError: liveShown ? requestError : null,
      allowance,
      draft,
      setDraft,
      send,
      stop,
      clearThread,
      ask,
      outcomeOf,
      resolve,
    }),
    [
      open,
      width,
      recipeId,
      blendId,
      batchId,
      threadId,
      merged,
      streaming,
      busy,
      liveShown,
      liveText,
      textQuiet,
      requestError,
      allowance,
      draft,
      send,
      stop,
      clearThread,
      ask,
      outcomeOf,
      resolve,
    ],
  );

  return <AssistantContext value={value}>{children}</AssistantContext>;
}

/**
 * The assistant, or an inert one when there is no provider.
 *
 * Same rule as `useSession`: returning a default rather than throwing keeps
 * every component testable in isolation, and matches the product rule that the
 * app works without the assistant.
 */
export function useAssistant(): AssistantState {
  return use(AssistantContext) ?? INERT;
}

/**
 * Tell the assistant which recipe is on screen.
 *
 * Called by the editor. Clearing on unmount is what makes leaving the editor
 * switch back to the global thread without the route having to know about it.
 */
export function useAssistantRecipe(recipeId: string | null): void {
  const { setRecipeId } = useAssistant();
  useEffect(() => {
    setRecipeId(recipeId);
    return () => setRecipeId(null);
  }, [recipeId, setRecipeId]);
}

/**
 * Tell the assistant which blending session is on screen.
 *
 * Same contract as {@link useAssistantRecipe}: clearing on unmount returns the
 * drawer to the global thread.
 */
export function useAssistantBlend(blendId: string | null): void {
  const { setBlendId } = useAssistant();
  useEffect(() => {
    setBlendId(blendId);
    return () => setBlendId(null);
  }, [blendId, setBlendId]);
}

/**
 * Tell the assistant which batch is on screen (PLAN-SCORESHEETS S6).
 *
 * Called by the batch page and by its Feedback page, so that "what did people
 * say about this beer" and the follow-up about what to change next time are one
 * conversation rather than two. Same contract as {@link useAssistantRecipe}:
 * clearing on unmount returns the drawer to the workspace thread.
 */
export function useAssistantBatch(batchId: string | null): void {
  const { setBatchId } = useAssistant();
  useEffect(() => {
    setBatchId(batchId);
    return () => setBatchId(null);
  }, [batchId, setBatchId]);
}

const INERT: AssistantState = {
  open: false,
  setOpen: () => undefined,
  toggle: () => undefined,
  width: DEFAULT_DRAWER_WIDTH,
  setWidth: () => undefined,
  recipeId: null,
  setRecipeId: () => undefined,
  blendId: null,
  setBlendId: () => undefined,
  batchId: null,
  setBatchId: () => undefined,
  threadId: threadIdFor(null),
  entries: [],
  streaming: false,
  busy: false,
  liveText: '',
  thinking: false,
  requestError: null,
  allowance: null,
  draft: '',
  setDraft: () => undefined,
  send: () => undefined,
  stop: () => undefined,
  clearThread: () => undefined,
  ask: () => undefined,
  outcomeOf: () => null,
  resolve: () => undefined,
};

// ---------------------------------------------------------------------------
// Persistence (best-effort, exactly like the sidebar's)
// ---------------------------------------------------------------------------

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Private browsing, quota, or no storage at all. Never worth failing over.
  }
}

function readFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
}

function readWidth(): number {
  try {
    const raw = Number.parseInt(localStorage.getItem(WIDTH_KEY) ?? '', 10);
    if (!Number.isFinite(raw)) return DEFAULT_DRAWER_WIDTH;
    return Math.min(MAX_DRAWER_WIDTH, Math.max(MIN_DRAWER_WIDTH, raw));
  } catch {
    return DEFAULT_DRAWER_WIDTH;
  }
}

function readResolved(): Record<string, ProposalOutcome> {
  try {
    const raw = localStorage.getItem(RESOLVED_KEY);
    if (raw === null) return {};
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null
      ? (parsed as Record<string, ProposalOutcome>)
      : {};
  } catch {
    return {};
  }
}

/**
 * The one rejection that is not a failure.
 *
 * `fetch` rejects an aborted request with a `DOMException` named `AbortError`,
 * and the name is all there is to go on — it is not an `Error` subclass anything
 * can be `instanceof`, and the message differs between browsers. Recognised
 * before {@link asRequestError} gets a look at it, because that function would
 * call this "we could not reach the assistant", which is a sentence about the
 * network rather than about the button the brewer just pressed.
 */
function isAbortError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { name?: unknown }).name === 'AbortError'
  );
}

/**
 * Drop a trailing question that never got an answer.
 *
 * The optimistic user entry is the only copy of a message whose send failed, so
 * it stays on screen to be retried from — and a retry re-sending it must not
 * leave the same question twice. Only a *trailing* user entry qualifies: one
 * with an answer, a chip or a notice after it is part of a turn that happened.
 */
function withoutUnansweredQuestion(live: readonly LiveEntry[]): readonly LiveEntry[] {
  return live.at(-1)?.type === 'user' ? live.slice(0, -1) : live;
}

function asRequestError(error: unknown): AiRequestError {
  if (typeof error === 'object' && error !== null && 'status' in error && 'message' in error) {
    return error as AiRequestError;
  }
  return {
    status: 0,
    code: 'offline',
    message: 'We could not reach the assistant. Check your connection.',
  };
}
